export type DepositDedupAction = 'submitted' | 'success' | 'failed' | 'incomplete'

const STORE_KEY = 'tm_dep_evt_fired'
const RECON_INIT_KEY = 'tm_dep_evt_recon_init'
const MAX_TOKENS = 300

const firedTokens = new Set<string>()

let userScope = ''

export const setDedupUserScope = (id?: string | number | null): void => {
  const next = id == null ? '' : String(id)
  if (next === '' || next === userScope) return
  userScope = next
  firedTokens.clear()
}

const storeKey = (): string => (userScope ? `${STORE_KEY}:${userScope}` : STORE_KEY)
const reconKey = (): string => (userScope ? `${RECON_INIT_KEY}:${userScope}` : RECON_INIT_KEY)

const toToken = (action: DepositDedupAction, id: string): string => `${action}:${id}`

const normaliseIds = (ids: (string | number | null | undefined)[]): string[] => {
  const result: string[] = []
  ids.forEach(id => {
    if (id == null) return
    const value = String(id).trim()
    if (value !== '') result.push(value)
  })
  return result
}

const readStore = (): string[] => {
  try {
    const raw = localStorage.getItem(storeKey())
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter((token): token is string => typeof token === 'string') : []
  } catch {
    return []
  }
}

const writeStore = (tokens: string[]): void => {
  try {
    const capped = tokens.length > MAX_TOKENS ? tokens.slice(tokens.length - MAX_TOKENS) : tokens
    localStorage.setItem(storeKey(), JSON.stringify(capped))
  } catch { /* noop */ }
}

export const isDedupStorageAvailable = (): boolean => {
  try {
    const probe = '__tm_dep_evt_probe__'
    localStorage.setItem(probe, '1')
    localStorage.removeItem(probe)
    return true
  } catch {
    return false
  }
}

export const hasDepositEventFired = (action: DepositDedupAction, ...ids: (string | number | null | undefined)[]): boolean => {
  const tokens = normaliseIds(ids).map(id => toToken(action, id))
  if (tokens.length === 0) return false
  if (tokens.some(token => firedTokens.has(token))) return true
  const stored = readStore()
  return tokens.some(token => stored.includes(token))
}

export const markDepositEventFiredMemory = (action: DepositDedupAction, ...ids: (string | number | null | undefined)[]): void => {
  normaliseIds(ids).map(id => toToken(action, id)).forEach(token => firedTokens.add(token))
}

export const persistDepositEventFired = (action: DepositDedupAction, ...ids: (string | number | null | undefined)[]): void => {
  const tokens = normaliseIds(ids).map(id => toToken(action, id))
  if (tokens.length === 0) return
  const stored = readStore()
  const merged = stored.slice()
  tokens.forEach(token => {
    if (!merged.includes(token)) merged.push(token)
  })
  if (merged.length !== stored.length) writeStore(merged)
}

export const markDepositEventFired = (action: DepositDedupAction, ...ids: (string | number | null | undefined)[]): void => {
  markDepositEventFiredMemory(action, ...ids)
  persistDepositEventFired(action, ...ids)
}

export const hasReconInitialised = (): boolean => {
  try {
    return !!localStorage.getItem(reconKey())
  } catch {
    return false
  }
}

export const setReconInitialised = (): void => {
  try {
    localStorage.setItem(reconKey(), '1')
  } catch { /* noop */ }
}
